// src/components/planning/EditReadingsModal/useReadingsForm.ts
import { useState } from 'react';
import { Reading, Service, ReadingUpdate } from '../../planning/ServiceCard/types';
import { bulkUpdateReadings } from '../../../services/readingService';

export const useReadingsForm = (
  readings: Reading,
  serviceId: number,
  onUpdate: (updatedService: Service) => void,
  onHide: () => void
) => {
  const [editedReadings, setEditedReadings] = useState<Reading>(readings);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertVariant, setAlertVariant] = useState<"success" | "danger">("success");
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field: keyof Reading, value: string) => {
    setEditedReadings(prev => ({
      ...prev,
      [field]: value // Remove the trim() here to allow spaces during typing
    }));
  };

  const handleUpdate = () => {
    const updates: ReadingUpdate = {};
    const fields: (keyof Reading)[] = ['ot_reading', 'psalm_reading', 'nt_reading', 'gospel_reading'];
    
    // Trim values only when preparing to save
    fields.forEach(field => {
      const trimmedValue = editedReadings[field]?.trim() || null;
      if (trimmedValue !== readings[field]) {
        updates[field] = trimmedValue;
      }
    });

    if (Object.keys(updates).length > 0) {
      setShowConfirm(true);
    } else {
      setAlertMessage("No changes to update");
      setAlertVariant("danger");
      setShowAlert(true);
    }
  };

  const handleConfirmUpdate = async () => {
    setIsLoading(true);
    setShowConfirm(false);

    try {
      const updates: ReadingUpdate = {
        ot_reading: editedReadings.ot_reading?.trim() || null,
        psalm_reading: editedReadings.psalm_reading?.trim() || null,
        nt_reading: editedReadings.nt_reading?.trim() || null,
        gospel_reading: editedReadings.gospel_reading?.trim() || null
      };

      const result = await bulkUpdateReadings(serviceId, updates);

      if (result.success && result.service) {
        setAlertMessage("Readings updated successfully!");
        setAlertVariant("success");
        onUpdate(result.service);
        
        setTimeout(() => {
          setShowAlert(false);
          onHide();
        }, 2000);
      } else {
        throw new Error(result.error || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating readings:', error);
      setAlertMessage(error instanceof Error ? error.message : 'Failed to update readings');
      setAlertVariant("danger");
    } finally {
      setIsLoading(false);
      setShowAlert(true);
    }
  };

  return {
    editedReadings,
    showConfirm,
    showAlert,
    alertMessage,
    alertVariant,
    isLoading,
    handleInputChange,
    handleUpdate,
    handleConfirmUpdate,
    setShowConfirm,
    setShowAlert
  };
};